import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, FileDown, Calendar, Trash2, ChevronDown, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  QUESTIONNAIRE_CONFIGS, 
  type QuestionnaireType
} from "@/data/welcomeQuestionnaireData";
import { generateQuestionnairePDF } from "@/utils/questionnairePdfExport";
import type { Json } from "@/integrations/supabase/types";

interface WelcomeQuestionnaire {
  id: string;
  appointment_id: string | null;
  patient_id: string | null;
  questionnaire_type: string;
  responses: Json;
  created_at: string;
  appointment?: {
    service_type: string | null;
    appointment_date: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

interface QuestionnairesSectionProps {
  patientId: string;
  patientName: string;
  patientEmail?: string;
  patientPhone?: string;
  onDataChange?: () => void;
}

export function QuestionnairesSection({ 
  patientId, 
  patientName,
  patientEmail,
  patientPhone,
  onDataChange
}: QuestionnairesSectionProps) {
  const [questionnaires, setQuestionnaires] = useState<WelcomeQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadQuestionnaires();
  }, [patientId]);

  const loadQuestionnaires = async () => {
    setLoading(true);
    try {
      const { data: directQuestionnaires, error: directError } = await supabase
        .from("welcome_questionnaires")
        .select(`
          *,
          appointment:appointments(
            service_type,
            appointment_date,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (directError) throw directError;

      const { data: patientAppointments } = await supabase
        .from("appointments")
        .select("id")
        .eq("patient_id", patientId);

      let appointmentQuestionnaires: any[] = [];
      if (patientAppointments && patientAppointments.length > 0) {
        const appointmentIds = patientAppointments.map(a => a.id);
        const { data: aptQuestionnaires, error: aptError } = await supabase
          .from("welcome_questionnaires")
          .select(`
            *,
            appointment:appointments(
              service_type,
              appointment_date,
              first_name,
              last_name,
              email,
              phone
            )
          `)
          .in("appointment_id", appointmentIds)
          .order("created_at", { ascending: false });

        if (!aptError && aptQuestionnaires) {
          appointmentQuestionnaires = aptQuestionnaires;
        }
      }

      const allQuestionnaires = [...(directQuestionnaires || [])];
      for (const q of appointmentQuestionnaires) {
        if (!allQuestionnaires.some(existing => existing.id === q.id)) {
          allQuestionnaires.push(q);
        }
      }

      allQuestionnaires.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setQuestionnaires(allQuestionnaires);
    } catch (error) {
      console.error("Error loading questionnaires:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async (questionnaire: WelcomeQuestionnaire) => {
    setExportingId(questionnaire.id);
    try {
      const appointment = questionnaire.appointment;
      const serviceTitle = appointment?.service_type || "Consultation";
      
      await generateQuestionnairePDF({
        patientName: appointment 
          ? `${appointment.first_name || ''} ${appointment.last_name || ''}`.trim() || patientName
          : patientName,
        patientEmail: appointment?.email || patientEmail || "",
        patientPhone: appointment?.phone || patientPhone || "",
        appointmentDate: appointment?.appointment_date 
          ? new Date(appointment.appointment_date).toLocaleDateString("fr-FR", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric"
            })
          : new Date(questionnaire.created_at).toLocaleDateString("fr-FR"),
        serviceTitle,
        questionnaireType: questionnaire.questionnaire_type,
        responses: (questionnaire.responses as Record<string, any>) || {},
      });
      
      toast.success("PDF généré avec succès");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setExportingId(null);
    }
  };

  const handleDeleteQuestionnaire = async (questionnaireId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce questionnaire ?")) return;
    
    setDeletingId(questionnaireId);
    try {
      const { error } = await supabase
        .from("welcome_questionnaires")
        .delete()
        .eq("id", questionnaireId);

      if (error) throw error;

      setQuestionnaires(prev => prev.filter(q => q.id !== questionnaireId));
      toast.success("Questionnaire supprimé avec succès");
      onDataChange?.();
    } catch (error) {
      console.error("Error deleting questionnaire:", error);
      toast.error("Erreur lors de la suppression du questionnaire");
    } finally {
      setDeletingId(null);
    }
  };

  const getQuestionnaireLabel = (type: string): string => {
    const config = QUESTIONNAIRE_CONFIGS[type as QuestionnaireType];
    return config?.title || "Questionnaire";
  };

  const getQuestionnaireColor = (type: string) => {
    switch (type) {
      case "naturopathy_adult":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200";
      case "naturopathy_children":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200";
      case "bach_flowers":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-200";
    }
  };

  const formatResponseValue = (question: any, value: any, detailValue?: string): string => {
    if (!value) return "Non renseigné";
    
    if (question.type === 'checkbox' && Array.isArray(value)) {
      return value
        .map(v => question.options?.find((opt: any) => opt.id === v)?.label || v)
        .join(', ');
    }
    if (question.type === 'scale') {
      return `${value}/10`;
    }
    if (question.type === 'conditionalText') {
      const selectedLabel = question.options?.find((opt: any) => opt.id === value)?.label || value;
      if (value === 'oui' && detailValue) {
        return `${selectedLabel} - ${detailValue}`;
      }
      return selectedLabel;
    }
    // Handle date objects {day, month, year}
    if (question.type === 'date' && typeof value === 'object' && value.day && value.month && value.year) {
      return `${String(value.day).padStart(2, '0')}/${String(value.month).padStart(2, '0')}/${value.year}`;
    }
    return String(value);
  };

  const countFilledResponses = (responses: Record<string, any>): number => {
    return Object.entries(responses).filter(([key, value]) => {
      if (key.endsWith('_detail')) return false;
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }).length;
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-8 text-center">
          <div className="text-muted-foreground">Chargement des questionnaires...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/20 dark:to-cyan-950/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-base sm:text-lg">Questionnaires de bienvenue</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {questionnaires.length === 0 
                ? "Aucun questionnaire enregistré" 
                : `${questionnaires.length} questionnaire${questionnaires.length > 1 ? "s" : ""} rempli${questionnaires.length > 1 ? "s" : ""}`
              }
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {questionnaires.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <ClipboardList className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">Aucun questionnaire de bienvenue enregistré</p>
            <p className="text-xs text-muted-foreground mt-1">Les questionnaires apparaîtront ici après la prise de rendez-vous</p>
          </div>
        ) : (
          <div className="space-y-3">
            {questionnaires.map((questionnaire) => {
              const config = QUESTIONNAIRE_CONFIGS[questionnaire.questionnaire_type as QuestionnaireType] 
                || QUESTIONNAIRE_CONFIGS.default;
              const appointment = questionnaire.appointment;
              const isExpanded = expandedId === questionnaire.id;
              const responseCount = countFilledResponses((questionnaire.responses as Record<string, any>) || {});
              
              return (
                <Card 
                  key={questionnaire.id} 
                  className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-teal-300 dark:ring-teal-700' : 'hover:shadow-md'}`}
                >
                  {/* Header - Always visible */}
                  <div 
                    className="p-3 sm:p-4 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : questionnaire.id)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 dark:text-teal-400" />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm sm:text-base">
                            {new Date(questionnaire.created_at).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <Badge variant="outline" className={`text-[10px] sm:text-xs ${getQuestionnaireColor(questionnaire.questionnaire_type)}`}>
                            {getQuestionnaireLabel(questionnaire.questionnaire_type)}
                          </Badge>
                        </div>
                        
                        {appointment?.service_type && (
                          <div className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
                            {appointment.service_type}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <ClipboardList className="w-3 h-3" />
                            {responseCount} réponse{responseCount > 1 ? "s" : ""}
                          </span>
                          {appointment?.appointment_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              RDV: {new Date(appointment.appointment_date).toLocaleDateString("fr-FR")}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Expand indicator */}
                      <div className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t">
                      <div className="p-3 sm:p-4 space-y-4">
                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportPDF(questionnaire);
                            }}
                            disabled={exportingId === questionnaire.id}
                            className="h-8 gap-2"
                          >
                            <FileDown className="w-4 h-4" />
                            {exportingId === questionnaire.id ? "Export..." : "Télécharger PDF"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteQuestionnaire(questionnaire.id);
                            }}
                            disabled={deletingId === questionnaire.id}
                            className="h-8 gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                            {deletingId === questionnaire.id ? "Suppression..." : "Supprimer"}
                          </Button>
                        </div>

                        {/* Appointment info */}
                        {appointment && (
                          <Card className="bg-muted/50 border-none">
                            <CardContent className="p-3">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">RDV:</span>
                                <span>
                                  {new Date(appointment.appointment_date).toLocaleDateString("fr-FR", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Responses */}
                        <div className="space-y-2">
                          <h5 className="font-medium text-sm text-muted-foreground">Réponses</h5>
                          <div className="grid gap-2">
                            {config.questions.map((question) => {
                              const responses = questionnaire.responses as Record<string, any>;
                              const value = responses[question.id];
                              const detailValue = responses[`${question.id}_detail`];
                              
                              if (question.id.endsWith('_autre') && !value) return null;
                              
                              if (question.id.endsWith('_autre')) {
                                const parentId = question.id.replace('_autre', '');
                                const parentValue = responses[parentId];
                                if (!Array.isArray(parentValue) || !parentValue.includes('autre')) return null;
                              }

                              const displayValue = formatResponseValue(question, value, detailValue);
                              if (displayValue === "Non renseigné") return null;
                              
                              return (
                                <div 
                                  key={question.id} 
                                  className="p-2 sm:p-3 bg-background rounded-lg border text-sm"
                                >
                                  <div className="font-medium text-xs sm:text-sm text-foreground/80">
                                    {question.label}
                                  </div>
                                  <div className="text-xs sm:text-sm text-foreground mt-1">
                                    {displayValue}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
